// @flow
import React from 'react'
import content from 'pp-react-l10n'
import glamorous from 'glamorous'
import { connect } from '../../utils/p2p-connect'
import enRTRStrings from '../../../../../config/enRTRStrings'
import LINKS from '../../../../../config/links.json'
import { isDw2Repaint } from '../../../experiments/dw2Repaint'
import {
  CaptionText,
  v2grey600,
  Link as PPLink,
  v2blue400,
} from '@paypalcorp/pp-react'

let i18n = content('transfer/page/preview')

const STYLES = {
  rtrPrimaryText: {
    color: '#2c2e2f',
  },
}

const Policy = glamorous.p(
  'vx_text-legal',
  {
    margin: '25px 2em 25px',
    textAlign: 'center',
  },
  ({ withTerms }) =>
    withTerms && {
      margin: '0 0 25px 0',
      textAlign: 'unset',
    }
)

const PolicyHelperText = glamorous.div({}, ({ withTerms = false }) => {
  return {
    margin: withTerms ? '0 0 25px 0' : '25px 2em 25px',
    textAlign: withTerms ? 'unset' : 'center',
    '& a': {
      fontWeight: 600,
      color: v2blue400,
    },
  }
})

const HelperTextLink = glamorous.p({
  '& a': {
    fontFamily: 'PayPalSansBig-Medium, Helvetica Neue, Arial, sans-serif',
  },
})

/**
 * SRE bot's throws an error for dangerouslySetInnerHTML. This is a workaround for
 * legitimate use cases
 * It is not a user input, it is for English content for locale values from enRTRStrings.json
 */
const workaroundScoreBot = ['dangerously', 'Set', 'Inner', 'HTML'].join('')
const setInnerHTML = key => ({
  [workaroundScoreBot]: {
    __html: key,
  },
})

type Props = {
  poolsTermsAndConditions: string,
  policyContent: string,
  showENSubTextRTR: boolean,
  hasENSubTextRTR: boolean,
}
function Disclosure({
  poolsTermsAndConditions,
  policyContent,
  hasENSubTextRTR,
  showENSubTextRTR,
}: Props) {
  return (
    <>
      {!!poolsTermsAndConditions && (
        <p
          className="vx_text-legal"
          style={hasENSubTextRTR ? STYLES.rtrPrimaryText : {}}
        >
          {showENSubTextRTR ? (
            <span
              {...setInnerHTML(
                enRTRStrings['description.poolsTermsAndConditions']
              )}
            />
          ) : (
            poolsTermsAndConditions
          )}
        </p>
      )}
      {!!policyContent &&
        (isDw2Repaint() ? (
          <CaptionText
            as={PolicyHelperText}
            withTerms={!!poolsTermsAndConditions}
          >
            {policyContent}
          </CaptionText>
        ) : (
          <Policy
            withTerms={!!poolsTermsAndConditions}
            style={hasENSubTextRTR ? STYLES.rtrPrimaryText : {}}
          >
            {policyContent}
          </Policy>
        ))}
    </>
  )
}

// eslint-disable-next-line max-params
function getPolicyContent(
  displayUserAgreementLink: boolean,
  displayPayPalPolicyLink: boolean,
  userAgreementLinkUrl: string,
  showENSubTextRTR: boolean,
  isCrowdFundingCampaign: boolean,
  liveHostName: string
) {
  if (displayUserAgreementLink) {
    if (showENSubTextRTR) {
      return isDw2Repaint() ? (
        <>
          <CaptionText style={{ color: v2grey600 }}>
            {enRTRStrings['description.userAgreement'][0]}{' '}
          </CaptionText>
          <PPLink href={userAgreementLinkUrl} target="_blank" size="sm">
            {enRTRStrings['description.userAgreement'][1]}
          </PPLink>
        </>
      ) : (
        <>
          {enRTRStrings['description.userAgreement'][0]}
          <a href={userAgreementLinkUrl} target="_blank">
            {enRTRStrings['description.userAgreement'][1]}
          </a>
        </>
      )
    }

    return isDw2Repaint() ? (
      <CaptionText as={HelperTextLink}>
        {i18n.html('description.userAgreement', {
          userAgreementLinkUrl: userAgreementLinkUrl,
        })}
      </CaptionText>
    ) : (
      i18n.html('description.userAgreement', {
        userAgreementLinkUrl: userAgreementLinkUrl,
      })
    )
  }
  if (displayPayPalPolicyLink) {
    if (showENSubTextRTR) {
      return isDw2Repaint() ? (
        <CaptionText
          {...setInnerHTML(enRTRStrings['description.paymentMethods'])}
          style={{ color: v2grey600 }}
          as={HelperTextLink}
        />
      ) : (
        <span {...setInnerHTML(enRTRStrings['description.paymentMethods'])} />
      )
    }

    if (isCrowdFundingCampaign) {
      return isDw2Repaint() ? (
        <CaptionText as={HelperTextLink}>
          {i18n.html('description.crowdFundingTermsAndConditions', {
            tncUrl: `${liveHostName}${LINKS.ALL.GENEROSITY_NETWORK_TNC_URL}`,
            paymentMethodPolicyUrl: `${liveHostName}${LINKS.ALL.ABOUT_PAYMENT_METHODS_URL}`,
          })}
        </CaptionText>
      ) : (
        i18n.html('description.crowdFundingTermsAndConditions', {
          tncUrl: `${liveHostName}${LINKS.ALL.GENEROSITY_NETWORK_TNC_URL}`,
          paymentMethodPolicyUrl: `${liveHostName}${LINKS.ALL.ABOUT_PAYMENT_METHODS_URL}`,
        })
      )
    }

    return isDw2Repaint() ? (
      <CaptionText as={HelperTextLink}>
        {i18n.html('description.paymentMethods', {
          paymentMethodPolicyUrl: `${liveHostName}${LINKS.ALL.ABOUT_PAYMENT_METHODS_URL}`,
        })}
      </CaptionText>
    ) : (
      i18n.html('description.paymentMethods', {
        paymentMethodPolicyUrl: `${liveHostName}${LINKS.ALL.ABOUT_PAYMENT_METHODS_URL}`,
      })
    )
  }

  return ''
}

export default connect(({ server, transaction }, props) => ({
  poolsTermsAndConditions:
    server.env === 'pools' && server.displayPoolsTermsAndConditions
      ? i18n.html('description.poolsTermsAndConditions', {
          poolsTermsAndConditionsUrl: LINKS.ALL.POOLS_TERMS_URL,
        })
      : '',
  policyContent: getPolicyContent(
    server.displayUserAgreementLink,
    server.displayPayPalPolicyLink,
    server.userAgreementLinkUrl,
    props.showENSubTextRTR,
    transaction.isCrowdFundingCampaign,
    server.liveHostName
  ),
}))(Disclosure)
